// creditReset — pure helpers for the monthly credit-refill clock.
//
// Extracted from `InShellUsageMeter` so the formula that derives the
// next refill moment can be unit-tested across calendar / DST edge
// cases without mounting the meter component.
//
// The contract intentionally matches the SQL-side `get_monthly_credits`
// helper: credits roll over at **00:00 UTC on the 1st of the next
// calendar month**, regardless of the viewer's timezone. Display in
// other zones is a render concern handled by `formatInTz`.

/**
 * Compute the next monthly refill moment for `now`.
 *
 * Always returns 00:00 UTC on the 1st of the *following* UTC calendar
 * month — even when `now` is itself exactly at a month boundary, in
 * which case the next refill is still a month away (we never return
 * `now` itself, otherwise `msUntilReset` would be 0 forever right at
 * the rollover instant).
 */
export function computeResetAt(now: Date): Date {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0),
  );
}

/**
 * Milliseconds between `now` and the next refill, clamped to 0.
 *
 * The clamp matters when callers pass a *cached* or server-supplied
 * `resetAt` that the local clock has already drifted past — e.g. a
 * memoised value held across the rollover instant, or a user whose
 * device clock is a few minutes ahead of UTC. Without the clamp the
 * UI would render negative durations like "-2h" or "-1d" until the
 * next compute tick.
 */
export function msUntilReset(
  now: Date,
  resetAt: Date = computeResetAt(now),
): number {
  return Math.max(0, resetAt.getTime() - now.getTime());
}

/**
 * True when the supplied `resetAt` is at or before `now` — i.e. the
 * local clock thinks the refill moment has already happened. The
 * canonical fix is to recompute `resetAt = computeResetAt(now)`, but
 * the meter still needs to render *something* during the brief window
 * where it's holding a stale value (memo, server fetch in flight, or
 * a clock-skew edge case at the rollover instant).
 */
export function isClockSkewed(now: Date, resetAt: Date): boolean {
  return resetAt.getTime() <= now.getTime();
}

/**
 * User-facing copy for the clock-skew / "we're refilling right now"
 * window. Centralised so the meter and any other surface can stay
 * in lockstep, and so the test suite can pin the exact wording.
 */
export const RESET_SKEW_LABEL =
  "Refilling now — check back in a moment";

/**
 * Break `msUntilReset` into a `{ days, hours, minutes }` triple using
 * the same flooring the meter uses for its compact "Xd Yh" string.
 */
export function breakdownUntilReset(ms: number): {
  days: number;
  hours: number;
  minutes: number;
} {
  return {
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
  };
}